import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { Modal, ModalConfirmFooter } from '../components/Modal.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { formatDateTime } from '../utils/formatDate.js';
import { runPdfDocumentImport } from '../utils/runPdfDocumentImport.js';
import { clientLabel } from '../utils/clientLabel.js';
import { SearchableSelect } from '../components/SearchableSelect.jsx';

const VISIBILITY_LABELS = {
  draft: 'Draft',
  team: 'Team',
  client: 'Client',
};

export function AdminPdfDocumentsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const caps = user?.capabilities ?? {};
  const [searchParams] = useSearchParams();
  const projectFilter = searchParams.get('project_id') || '';
  const taskFilter = searchParams.get('task_id') || '';
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [showBlank, setShowBlank] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [title, setTitle] = useState('');
  const [clientId, setClientId] = useState('');
  const [projectId, setProjectId] = useState(projectFilter);
  const [taskId, setTaskId] = useState(taskFilter);
  const [pages, setPages] = useState(1);
  const [analysisUrl, setAnalysisUrl] = useState('');
  const [file, setFile] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const projectOptions = useMemo(() => {
    if (!clientId) return projects;
    return projects.filter((p) => String(p.client_id) === String(clientId));
  }, [projects, clientId]);

  const resetMeta = () => {
    setTitle('');
    setClientId('');
    setProjectId(projectFilter);
    setTaskId(taskFilter);
    setPages(1);
    setAnalysisUrl('');
    setFile(null);
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (projectFilter) params.set('project_id', projectFilter);
      if (taskFilter) params.set('task_id', taskFilter);
      if (q.trim()) params.set('q', q.trim());
      const qs = params.toString() ? `?${params}` : '';
      const [docs, projectList, clientList] = await Promise.all([
        apiJson(`/api/admin/pdf-documents${qs}`),
        apiJson('/api/admin/projects'),
        apiJson('/api/admin/clients'),
      ]);
      setItems(docs);
      setProjects(projectList);
      setClients(clientList);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [projectFilter, taskFilter]);

  const attachmentFields = (
    <>
      <label className="field">
        <span>Client</span>
        <SearchableSelect value={clientId} onChange={(e) => { setClientId(e.target.value); setProjectId(''); }}>
          <option value="">None</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{clientLabel(c)}</option>
          ))}
        </SearchableSelect>
      </label>
      <label className="field">
        <span>Project</span>
        <SearchableSelect value={projectId} onChange={(e) => setProjectId(e.target.value)}>
          <option value="">None</option>
          {projectOptions.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </SearchableSelect>
      </label>
      <label className="field">
        <span>Task ID (optional)</span>
        <input value={taskId} onChange={(e) => setTaskId(e.target.value)} placeholder="Attach to task by ID" />
      </label>
    </>
  );

  const metaPayload = () => {
    const body = {};
    if (title.trim()) body.title = title.trim();
    if (clientId) body.client_id = Number(clientId);
    if (projectId) body.project_id = Number(projectId);
    if (taskId) body.task_id = Number(taskId);
    return body;
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Choose a PDF file.');
      return;
    }
    setBusy(true);
    setError('');
    setUploadStatus('Uploading PDF…');
    try {
      const form = new FormData();
      form.append('file', file);
      Object.entries(metaPayload()).forEach(([k, v]) => form.append(k, String(v)));
      const created = await apiJson('/api/admin/pdf-documents', { method: 'POST', body: form });
      if (!created?.id) {
        throw new Error('Upload succeeded but the document id was missing. Refresh the list and open it manually.');
      }
      await runPdfDocumentImport(created.id, {
        title: created.title || title.trim() || file.name,
        onStatus: setUploadStatus,
      });
      setShowUpload(false);
      resetMeta();
      setUploadStatus('');
      navigate(`/admin/pdf-documents/${created.id}`);
    } catch (err) {
      setError(err.message);
      setUploadStatus('');
    } finally {
      setBusy(false);
    }
  };

  const createBlank = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const created = await apiJson('/api/admin/pdf-documents/blank', {
        method: 'POST',
        body: {
          ...metaPayload(),
          pages: Number(pages) || 1,
          title: title.trim() || 'Untitled document',
        },
      });
      if (!created?.id) {
        throw new Error('Document was created but the id was missing. Refresh the list and open it manually.');
      }
      setShowBlank(false);
      resetMeta();
      navigate(`/admin/pdf-documents/${created.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const createAnalysis = async (e) => {
    e.preventDefault();
    if (!analysisUrl.trim()) {
      setError('Enter a website URL to analyze.');
      return;
    }
    if (!caps.openai) {
      setError('OpenAI plan feature is required. Configure the key under Integrations.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const created = await apiJson('/api/admin/pdf-documents/site-analysis', {
        method: 'POST',
        body: {
          ...metaPayload(),
          url: analysisUrl.trim(),
          title: title.trim() || undefined,
        },
      });
      if (!created?.id) {
        throw new Error('Analysis finished but the document id was missing. Refresh the list and open it manually.');
      }
      setShowAnalysis(false);
      resetMeta();
      navigate(`/admin/pdf-documents/${created.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await apiJson(`/api/admin/pdf-documents/${toDelete.id}`, { method: 'DELETE' });
      setToDelete(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className="page-header-row">
        <div>
          <h2 style={{ marginTop: 0 }} data-tour="page-title">PDF documents</h2>
          <p className="muted small">Create blank PDFs, website analysis reports, or upload existing files.</p>
        </div>
        <div className="pdf-editor-actions">
          <button type="button" className="btn btn-outline-secondary" onClick={() => { resetMeta(); setShowBlank(true); }}>
            Create blank
          </button>
          <button type="button" className="btn btn-outline-primary" onClick={() => { resetMeta(); setShowAnalysis(true); }}>
            Create analysis
          </button>
          <button type="button" className="btn btn-primary" onClick={() => { resetMeta(); setShowUpload(true); }}>
            Upload PDF
          </button>
        </div>
      </div>

      {error ? <p className="err">{error}</p> : null}

      <form
        className="auth-card flat row-gap"
        style={{ marginBottom: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'end' }}
        onSubmit={(e) => {
          e.preventDefault();
          void load();
        }}
      >
        <label className="field" style={{ flex: '1 1 220px', marginBottom: 0 }}>
          <span>Search</span>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Title or file name" />
        </label>
        <button type="submit" className="btn btn-outline-secondary">Filter</button>
      </form>

      {loading ? (
        <p className="muted">Loading…</p>
      ) : items.length === 0 ? (
        <div className="auth-card flat">
          <p className="muted" style={{ marginBottom: 0 }}>
            No PDF documents yet. Create a blank PDF, run a website analysis, or upload a file.
          </p>
        </div>
      ) : (
        <div className="auth-card flat table-responsive">
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th>Title</th>
                <th>Attached to</th>
                <th>Visibility</th>
                <th>Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((doc) => (
                <tr key={doc.id}>
                  <td>
                    <Link to={`/admin/pdf-documents/${doc.id}`}>{doc.title}</Link>
                    <div className="muted small">{doc.file_name}</div>
                  </td>
                  <td className="small">
                    {doc.project_name ? <div>Project: {doc.project_name}</div> : null}
                    {doc.task_title ? <div>Task: {doc.task_title}</div> : null}
                    {doc.client_name ? <div>Client: {doc.client_name}</div> : null}
                    {!doc.project_name && !doc.task_title && !doc.client_name ? (
                      <span className="muted">Unattached</span>
                    ) : null}
                  </td>
                  <td>{VISIBILITY_LABELS[doc.visibility] || doc.visibility}</td>
                  <td className="small muted">{formatDateTime(doc.updated_at)}</td>
                  <td className="text-end">
                    <Link className="btn btn-sm btn-outline-primary me-2" to={`/admin/pdf-documents/${doc.id}`}>
                      Edit
                    </Link>
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setToDelete(doc)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={showBlank} title="Create blank PDF" onClose={() => !busy && setShowBlank(false)}>
        <form className="stack" onSubmit={createBlank}>
          <label className="field">
            <span>Title</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Untitled document" />
          </label>
          <label className="field">
            <span>Pages</span>
            <input type="number" min={1} max={20} value={pages} onChange={(e) => setPages(e.target.value)} />
          </label>
          {attachmentFields}
          <div className="modal-actions">
            <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => setShowBlank(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Creating…' : 'Create blank'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={showAnalysis} title="Create website analysis" onClose={() => !busy && setShowAnalysis(false)} size="lg">
        <form className="stack" onSubmit={createAnalysis}>
          <p className="muted small" style={{ marginTop: 0 }}>
            Paste a website URL. The portal will crawl pages on that domain and ask OpenAI to generate an improvement report as a PDF.
            This can take up to a couple of minutes.
          </p>
          {!caps.openai ? (
            <p className="err">OpenAI is required. Enable the plan feature and add an API key under Integrations.</p>
          ) : null}
          <label className="field">
            <span>Website URL</span>
            <input
              value={analysisUrl}
              onChange={(e) => setAnalysisUrl(e.target.value)}
              placeholder="https://example.com"
              required
            />
          </label>
          <label className="field">
            <span>Report title (optional)</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Website improvement analysis" />
          </label>
          {attachmentFields}
          <div className="modal-actions">
            <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => setShowAnalysis(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy || !caps.openai}>
              {busy ? 'Analyzing site…' : 'Create analysis'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={showUpload} title="Upload PDF" onClose={() => !busy && setShowUpload(false)}>
        <form className="stack" onSubmit={upload}>
          <label className="field">
            <span>PDF file</span>
            <input type="file" accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} required />
          </label>
          <label className="field">
            <span>Title</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Optional — defaults to file name" />
          </label>
          {attachmentFields}
          {uploadStatus ? <p className="muted small">{uploadStatus}</p> : null}
          <div className="modal-actions">
            <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => setShowUpload(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? (uploadStatus || 'Working…') : 'Upload & import once'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={Boolean(toDelete)}
        title="Delete PDF?"
        onClose={() => !deleting && setToDelete(null)}
        footer={
          <ModalConfirmFooter
            confirmLabel="Delete"
            confirmBusy={deleting}
            onCancel={() => setToDelete(null)}
            onConfirm={confirmDelete}
            danger
          />
        }
      >
        <p>Delete <strong>{toDelete?.title}</strong>? This cannot be undone.</p>
      </Modal>
    </div>
  );
}
